<template>
  <nav class="top-menu" aria-label="Menu principal de navegação">
    <!-- Logo / Brand -->
    <div class="menu-brand">
      <router-link to="/inicio" class="brand-link" aria-label="Início">
        <img
          src="../assets/LogoSemEscritaCentralizado.png"
          alt="PetMob"
          class="brand-logo"
        />
      </router-link>
    </div>

    <!-- Indicador enquanto menus estão sendo recuperados (rede lenta / pico de energia) -->
    <div v-if="menusCarregando" class="menu-recuperando" aria-live="polite">
      <span class="menu-recuperando-ponto" />
      <span class="menu-recuperando-ponto" />
      <span class="menu-recuperando-ponto" />
    </div>

    <!-- Grupo Principal -->
    <div class="menu-group">
      <router-link
        v-for="m in gruposPrincipal"
        :key="m.chave"
        :to="m.rota"
        class="nav-button"
        active-class="active-button"
        :aria-label="`Ir para ${m.label}`"
      >
        <component :is="icones[m.icone]" class="icon" />
        <span class="nav-label">{{ m.label }}</span>
      </router-link>
    </div>

    <!-- Separador -->
    <div
      v-if="gruposClientes.length > 0 || gruposGestao.length > 0"
      class="menu-separator"
    />

    <!-- Dropdown Clientes -->
    <div
      v-if="gruposClientes.length > 0"
      class="menu-dropdown"
      @mouseenter="abrirDropdown('clientes')"
      @mouseleave="fecharDropdown"
    >
      <button
        class="nav-button dropdown-trigger"
        :class="{ 'active-button': grupoAtivo === 'clientes' }"
      >
        <PawPrint class="icon" />
        <span class="nav-label">Clientes</span>
        <ChevronDown
          class="chevron"
          :class="{ aberto: dropdownAberto === 'clientes' }"
        />
      </button>
      <transition name="dropdown">
        <div v-if="dropdownAberto === 'clientes'" class="dropdown-menu">
          <router-link
            v-for="m in gruposClientes"
            :key="m.chave"
            :to="m.rota"
            class="dropdown-item"
            active-class="dropdown-item-active"
            @click="fecharDropdown"
          >
            <component :is="icones[m.icone]" class="icon-sm" />
            {{ m.label }}
          </router-link>
        </div>
      </transition>
    </div>

    <!-- Dropdown Gestão -->
    <div
      v-if="gruposGestao.length > 0"
      class="menu-dropdown"
      @mouseenter="abrirDropdown('gestao')"
      @mouseleave="fecharDropdown"
    >
      <button
        class="nav-button dropdown-trigger"
        :class="{ 'active-button': grupoAtivo === 'gestao' }"
      >
        <LayoutDashboard class="icon" />
        <span class="nav-label">Gestão</span>
        <ChevronDown
          class="chevron"
          :class="{ aberto: dropdownAberto === 'gestao' }"
        />
      </button>
      <transition name="dropdown">
        <div v-if="dropdownAberto === 'gestao'" class="dropdown-menu">
          <router-link
            v-for="m in gruposGestao"
            :key="m.chave"
            :to="m.rota"
            class="dropdown-item"
            active-class="dropdown-item-active"
            @click="fecharDropdown"
          >
            <component :is="icones[m.icone]" class="icon-sm" />
            {{ m.label }}
          </router-link>
        </div>
      </transition>
    </div>

    <!-- Ações direita -->
    <div class="menu-right">
      <UnidadeSelector v-if="store.temFuncionalidade('multi_unidade')" />

      <!-- Dropdown Configurações -->
      <div
        class="menu-dropdown"
        @mouseenter="abrirDropdown('configuracoes')"
        @mouseleave="fecharDropdown"
      >
        <button
          class="nav-button dropdown-trigger"
          :class="{ 'active-button': grupoAtivo === 'configuracoes' }"
          aria-label="Configurações"
        >
          <Settings class="icon" />
          <span class="nav-label">Config.</span>
          <ChevronDown
            class="chevron"
            :class="{ aberto: dropdownAberto === 'configuracoes' }"
          />
        </button>
        <transition name="dropdown">
          <div
            v-if="dropdownAberto === 'configuracoes'"
            class="dropdown-menu dropdown-menu--right"
          >
            <router-link
              v-for="item in configItens"
              :key="item.rota"
              :to="item.rota"
              class="dropdown-item"
              active-class="dropdown-item-active"
              @click="fecharDropdown"
            >
              <component :is="item.icone" class="icon-sm" />
              {{ item.label }}
            </router-link>
          </div>
        </transition>
      </div>

      <button
        class="nav-button logoff-button"
        @click="logoff"
        aria-label="Sair do sistema"
      >
        <LogOut class="icon" />
        <span class="nav-label">Sair</span>
      </button>
    </div>
  </nav>
</template>

<script setup>
import { ref, computed, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useGlobalStore } from "@/store/useGlobalStore";
import UnidadeSelector from "@/components/UnidadeSelector.vue";
import {
  Home,
  Calendar,
  CheckCircle,
  Activity,
  ClipboardList,
  Star,
  Gift,
  Users,
  BarChart2,
  Package,
  Layers,
  Ticket,
  Scissors,
  Tag,
  Settings,
  LogOut,
  ChevronDown,
  PawPrint,
  LayoutDashboard,
  Building2,
  Clock,
  GitCompareArrows,
  Settings2,
  Info,
  CreditCard,
  Zap,
  FileText,
  Send,
  ShoppingCart,
} from "lucide-vue-next";

const route = useRoute();
const router = useRouter();
const store = useGlobalStore();

const dropdownAberto = ref(null);
const menusCarregando = ref(false);

// Auto-retry: se os menus chegarem vazios (ex: falha de rede após pico de energia),
// tenta buscar na API com até 3 tentativas, usando catálogo estático como última defesa.
// Isso garante que o menu NUNCA fique em branco para um usuário autenticado.
const _tentativas = ref(0);
const MAX_TENTATIVAS = 3;

async function _tentarRecarregarMenus() {
  if (_tentativas.value >= MAX_TENTATIVAS || !store.cnpjLogado) return;
  _tentativas.value++;
  menusCarregando.value = true;
  try {
    const { default: planosService } = await import("@/services/planosService");
    const itens = await planosService.buscarMenuAcessos(store.cnpjLogado);
    if (Array.isArray(itens) && itens.length > 0) {
      store.definirMenus(itens);
    } else {
      store.construirMenusDeFallback();
    }
  } catch {
    store.construirMenusDeFallback();
  } finally {
    menusCarregando.value = false;
    // Se ainda vazio após tentativa, agenda a próxima com espera exponencial
    if (store.menus.length === 0 && _tentativas.value < MAX_TENTATIVAS) {
      setTimeout(_tentarRecarregarMenus, 2000 * _tentativas.value);
    }
  }
}

watch(
  () => store.menus.length,
  (len) => {
    if (len === 0 && store.cnpjLogado && _tentativas.value < MAX_TENTATIVAS) {
      setTimeout(_tentarRecarregarMenus, 800);
    }
  },
  { immediate: true },
);

// Mapa de nome (string do banco) → componente de ícone
const icones = {
  Home,
  Calendar,
  CheckCircle,
  Activity,
  ClipboardList,
  Star,
  Gift,
  Users,
  BarChart2,
  Package,
  Layers,
  Ticket,
  Scissors,
  Tag,
  Settings,
  PawPrint,
  LayoutDashboard,
  Send,
  ShoppingCart,
};

// Menus vêm da API via store, com fallback estático e sessionStorage como resiliência.
const gruposPrincipal = computed(() =>
  store.menus
    .filter((m) => m.grupo === "principal")
    .sort((a, b) => a.ordem - b.ordem),
);
const gruposClientes = computed(() =>
  store.menus
    .filter((m) => m.grupo === "clientes")
    .sort((a, b) => a.ordem - b.ordem),
);
const gruposGestao = computed(() =>
  store.menus
    .filter((m) => m.grupo === "gestao")
    .sort((a, b) => a.ordem - b.ordem),
);

const configItens = [
  {
    label: "Dados da Empresa",
    rota: "/configuracoes/dados-petshop",
    icone: Building2,
  },
  {
    label: "Horários de Funcionamento",
    rota: "/configuracoes/horarios-funcionamento",
    icone: Clock,
  },
  {
    label: "Link de Agendamentos",
    rota: "/configuracoes/linkAgendamentos",
    icone: GitCompareArrows,
  },
  {
    label: "Parâmetros da Empresa",
    rota: "/configuracoes/parametros-empresa",
    icone: Settings2,
  },
  { label: "Fale Conosco", rota: "/configuracoes/FaleConosco", icone: Info },
  {
    label: "Financeiro / Recebimentos",
    rota: "/configuracoes/financeiro",
    icone: CreditCard,
  },
  {
    label: "Minha Assinatura",
    rota: "/configuracoes/assinatura",
    icone: CreditCard,
  },
  { label: "Boost de Visibilidade", rota: "/configuracoes/boost", icone: Zap },
  { label: "Meu Contrato", rota: "/configuracoes/contrato", icone: FileText },
];

const grupoAtivo = computed(() => {
  if (route.path.startsWith("/configuracoes")) return "configuracoes";
  return route.meta?.grupo ?? null;
});

function abrirDropdown(nome) {
  dropdownAberto.value = nome;
}
function fecharDropdown() {
  dropdownAberto.value = null;
}
function logoff() {
  store.logout();
  router.push("/login");
}
</script>

<style scoped>
.top-menu {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0 1rem;
  height: 56px;
  background: var(--color-surface);
  border-bottom: 1px solid var(--color-border);
  position: sticky;
  top: 0;

  /* Indicador de recuperação de menus */
  .menu-recuperando {
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 0 0.75rem;
  }
  .menu-recuperando-ponto {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: var(--color-text-muted);
    animation: pulse-dot 1.2s ease-in-out infinite;
  }
  .menu-recuperando-ponto:nth-child(2) {
    animation-delay: 0.2s;
  }
  .menu-recuperando-ponto:nth-child(3) {
    animation-delay: 0.4s;
  }
  @keyframes pulse-dot {
    0%,
    80%,
    100% {
      opacity: 0.3;
      transform: scale(0.8);
    }
    40% {
      opacity: 1;
      transform: scale(1);
    }
  }
  z-index: 100;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
}

/* Brand */
.menu-brand {
  margin-right: 0.75rem;
}
.brand-link {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  text-decoration: none;
}
.brand-logo {
  height: 32px;
  width: auto;
  display: block;
}

/* Grupo inline */
.menu-group {
  display: flex;
  align-items: center;
  gap: 0.125rem;
}

.menu-separator {
  width: 1px;
  height: 24px;
  background: var(--color-border);
  margin: 0 0.5rem;
}

/* Botões de nav */
.nav-button {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.4rem 0.7rem;
  border-radius: var(--radius-md);
  border: none;
  background: transparent;
  color: var(--color-text-muted);
  font-size: 0.82rem;
  font-weight: 500;
  cursor: pointer;
  text-decoration: none;
  transition: background 0.15s, color 0.15s;
  white-space: nowrap;
}
.nav-button:hover {
  background: var(--color-bg);
  color: var(--color-text);
}
.active-button {
  background: var(
    --color-primary-light,
    rgba(var(--color-primary-rgb, 99, 102, 241), 0.1)
  );
  color: var(--color-primary);
  font-weight: 600;
}

.icon {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
}
.icon-sm {
  width: 14px;
  height: 14px;
  flex-shrink: 0;
}
.chevron {
  width: 13px;
  height: 13px;
  margin-left: 0.1rem;
  transition: transform 0.2s;
}
.chevron.aberto {
  transform: rotate(180deg);
}

/* Dropdown */
.menu-dropdown {
  position: relative;
}
.dropdown-trigger {
  display: flex;
  align-items: center;
}
.dropdown-menu {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  min-width: 200px;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
  padding: 0.375rem;
  z-index: 200;
}
.dropdown-menu--right {
  left: auto;
  right: 0;
  min-width: 220px;
}
.dropdown-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.45rem 0.6rem;
  border-radius: var(--radius-sm);
  font-size: 0.82rem;
  font-weight: 500;
  color: var(--color-text-muted);
  text-decoration: none;
  transition: background 0.12s, color 0.12s;
}
.dropdown-item:hover {
  background: var(--color-bg);
  color: var(--color-text);
}
.dropdown-item-active {
  background: var(--color-primary-light, rgba(99, 102, 241, 0.1));
  color: var(--color-primary);
  font-weight: 600;
}

/* Direita */
.menu-right {
  display: flex;
  align-items: center;
  gap: 0.125rem;
  margin-left: auto;
}
.logoff-button {
  color: var(--color-danger, #ef4444);
}
.logoff-button:hover {
  background: rgba(239, 68, 68, 0.08);
  color: var(--color-danger, #ef4444);
}

/* Animação dropdown */
.dropdown-enter-active,
.dropdown-leave-active {
  transition: opacity 0.15s, transform 0.15s;
}
.dropdown-enter-from,
.dropdown-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}
</style>
